import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { initializeLocalDb } from '../lib/localDb';
import { syncManager } from '../lib/syncManager';
import { db } from '../lib/localDb';
import { supabase } from '../lib/supabase';
import { getLastResyncCheck, setLastResyncCheck } from '../lib/storage';

interface InitialSyncProps {
  onComplete: () => void;
}

export function InitialSync({ onComplete }: InitialSyncProps) {
  const { user } = useAuth();
  const [status, setStatus] = useState('Initializing local database...');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const performInitialSync = async () => {
      try {
        // Initialize local database
        await initializeLocalDb();
        setStatus('Checking for system updates...');

        // Check for force resync
        const lastResyncCheckTime = getLastResyncCheck();
        const { data: systemConfig } = await supabase
          .from('system_config')
          .select('value')
          .eq('key', 'force_resync')
          .single();

        if (systemConfig?.value?.timestamp) {
          const forceResyncTime = new Date(systemConfig.value.timestamp).getTime();
          const lastCheckTime = lastResyncCheckTime ? new Date(lastResyncCheckTime).getTime() : 0;

          if (forceResyncTime > lastCheckTime) {
            setStatus('System update detected. Clearing local data...');
            // Clear all local data
            await db.delete();
            await db.open();
            await initializeLocalDb();
            // Update last check time
            setLastResyncCheck(new Date().toISOString());
          }
        }

        // Check if we have any local data
        const cardsCount = await db.cards.count();
        
        if (cardsCount === 0) {
          // First time user or cleared data - perform initial sync
          setStatus('Downloading your flashcards... This might take a minute.');
          await syncManager.initialSync(user.id);
        } else {
          // Existing data - just sync changes
          setStatus('Syncing recent changes...');
          await syncManager.syncAll();
        }

        // Start periodic sync
        syncManager.startPeriodicSync();
        
        // Complete
        onComplete();
      } catch (err) {
        console.error('Initial sync error:', err);
        setError(String(err));
        
        // Still allow offline usage even if sync fails
        setTimeout(onComplete, 2000);
      }
    };

    performInitialSync();

    return () => {
      // Cleanup on unmount
      syncManager.stopPeriodicSync();
    };
  }, [user, onComplete]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-2">Sync Error</div>
          <div className="text-sm text-gray-600 mb-4">{error}</div>
          <div className="text-sm text-gray-500">Continuing in offline mode...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="text-lg mb-2">{status}</div>
        <div className="text-sm text-gray-500">Please wait...</div>
      </div>
    </div>
  );
}